import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { promozioniAPI, productsAPI, clientsAPI } from '../utils/api';
import { PromozioneCliente, TipoPromozione, TargetPromozione } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import PromozioneAllegatiManager from '../components/PromozioneAllegatiManager';
import ProductSearchCombobox, { useProdotto } from '../components/ProductSearchCombobox';
import BrandMultiSelect from '../components/BrandMultiSelect';
import ColorChipPicker from '../components/ColorChipPicker';
import { canaliAPI, settoriMerceologiciAPI } from '@offertify/api-client';
import { contrastColor } from '../utils/color';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Award,
  Calendar,
  Euro,
  Loader2,
  LucideIcon,
  Package,
  Paperclip,
  Percent,
  Save,
  Search,
  Tags,
  UserPlus,
  X,
} from 'lucide-react';

interface PromoFormData {
  nome: string;
  codice: string;
  descrizione: string;
  tipo: TipoPromozione;
  target_tipo: TargetPromozione;
  valore: string;
  target_prodotto_id: string;
  target_categoria: string;
  target_marche: string[];
  valido_da: string;
  valido_a: string;
  is_attiva: boolean;
  priorita: string;
  impatta_prezzo: boolean;
  canale_ids: string[];
  settore_ids: string[];
}

const initialFormData: PromoFormData = {
  nome: '',
  codice: '',
  descrizione: '',
  tipo: 'sconto_percentuale',
  target_tipo: 'prodotto',
  valore: '',
  target_prodotto_id: '',
  target_categoria: '',
  target_marche: [],
  valido_da: new Date().toISOString().split('T')[0],
  valido_a: '',
  is_attiva: true,
  priorita: '0',
  impatta_prezzo: true,
  canale_ids: [],
  settore_ids: [],
};

const TIPO_OPTIONS: {
  value: TipoPromozione;
  label: string;
  description: string;
  icon: LucideIcon;
}[] = [
  {
    value: 'sconto_percentuale',
    label: 'Sconto percentuale',
    description: 'Il prezzo viene ridotto di una percentuale (es. -10%).',
    icon: Percent,
  },
  {
    value: 'prezzo_fisso',
    label: 'Prezzo fisso',
    description: "L'articolo viene venduto a un prezzo fisso.",
    icon: Euro,
  },
  {
    value: 'tag_temporale',
    label: 'Tag temporale',
    description: "Etichetta manuale dell'offerta, senza effetti su prezzi o quantità.",
    icon: Tags,
  },
];

const TARGET_OPTIONS: {
  value: TargetPromozione;
  label: string;
  description: string;
  icon: LucideIcon;
}[] = [
  {
    value: 'prodotto',
    label: 'Prodotto',
    description: 'Un singolo articolo del catalogo.',
    icon: Package,
  },
  {
    value: 'categoria',
    label: 'Categoria',
    description: 'Tutti gli articoli di una categoria.',
    icon: Tags,
  },
  {
    value: 'marca',
    label: 'Marca',
    description: 'Tutti gli articoli di uno o più marchi.',
    icon: Award,
  },
];

const TARGET_ICONS: Record<TargetPromozione, LucideIcon> = {
  offerta: Tags,
  prodotto: Package,
  categoria: Tags,
  marca: Award,
};

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm disabled:bg-gray-50 disabled:text-gray-500';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
const hintClass = 'mt-1.5 text-xs text-gray-500';

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-6 py-4">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        {description && <p className="mt-0.5 text-sm text-gray-500">{description}</p>}
      </div>
      <div className="space-y-5 px-6 py-5">{children}</div>
    </section>
  );
}

function OptionCard({
  selected,
  disabled,
  onClick,
  icon: Icon,
  title,
  description,
}: {
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={`flex items-start gap-3 rounded-xl border p-3.5 text-left transition-colors ${
        selected
          ? 'border-blue-600 bg-blue-50/60 ring-1 ring-blue-600'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
      } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
    >
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors ${
          selected ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'
        }`}
      >
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-gray-900">{title}</span>
        <span className="mt-0.5 block text-xs leading-snug text-gray-500">{description}</span>
      </span>
    </button>
  );
}

function Toggle({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
        checked ? 'bg-blue-600' : 'bg-gray-200'
      } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

function formatDate(d: string) {
  return new Date(`${d}T00:00:00`).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function PromotionEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = !id || id === 'new';
  const { hasPermission } = usePermissions();
  const canEdit = isNew
    ? hasPermission('promozioni:create')
    : hasPermission('promozioni:edit');
  const readOnly = !canEdit;

  const [formData, setFormData] = useState<PromoFormData>(initialFormData);
  const [showClientModal, setShowClientModal] = useState(false);
  const [clientSearch, setClientSearch] = useState('');

  // Fetch existing promotion
  const { data: promozione, isLoading: loadingPromo } = useQuery({
    queryKey: ['promozione', id],
    queryFn: async () => {
      const res = await promozioniAPI.getPromozione(id!);
      return res.data;
    },
    enabled: !isNew,
  });

  // Fetch assigned clients
  const { data: clientiData, refetch: refetchClienti } = useQuery({
    queryKey: ['promozione-clienti', id],
    queryFn: async () => {
      const res = await promozioniAPI.getClienti(id!);
      return res.data.data as PromozioneCliente[];
    },
    enabled: !isNew,
  });

  // Fetch categories and brands for dropdowns
  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await productsAPI.getCategories();
      return res.data;
    },
  });

  const { data: brands } = useQuery({
    queryKey: ['brands'],
    queryFn: async () => {
      const res = await productsAPI.getBrands();
      return res.data;
    },
  });

  // Canali / settori merceologici per la classificazione della promozione
  const canViewCanali = hasPermission('canali:view');
  const canViewSettori = hasPermission('settori:view');
  const { data: canali } = useQuery({
    queryKey: ['canali-select'],
    queryFn: async () => (await canaliAPI.getCanaliForSelect()).data,
    enabled: canViewCanali,
  });
  const { data: settori } = useQuery({
    queryKey: ['settori-merceologici-select'],
    queryFn: async () => (await settoriMerceologiciAPI.getSettoriForSelect()).data,
    enabled: canViewSettori,
  });

  // Fetch all clients for assignment
  const { data: allClientsRes } = useQuery({
    queryKey: ['clients-select'],
    queryFn: () => clientsAPI.getClientsForSelect(),
  });

  // Selected target product (shared cache with the combobox)
  const { data: targetProduct } = useProdotto(
    formData.target_tipo === 'prodotto' ? formData.target_prodotto_id || undefined : undefined
  );

  const allClients = allClientsRes?.data || [];
  const assignedClients = clientiData || [];

  // Populate form when editing
  useEffect(() => {
    if (promozione) {
      setFormData({
        nome: promozione.nome,
        codice: promozione.codice || '',
        descrizione: promozione.descrizione || '',
        tipo: promozione.tipo,
        target_tipo: promozione.target_tipo,
        valore: String(promozione.valore),
        target_prodotto_id: promozione.target_prodotto_id || '',
        target_categoria: promozione.target_categoria || '',
        target_marche:
          promozione.target_marche && promozione.target_marche.length > 0
            ? promozione.target_marche
            : promozione.target_marca
              ? [promozione.target_marca]
              : [],
        valido_da: promozione.valido_da,
        valido_a: promozione.valido_a || '',
        is_attiva: promozione.is_attiva,
        priorita: String(promozione.priorita),
        impatta_prezzo: promozione.impatta_prezzo ?? true,
        canale_ids: (promozione.canali || []).map((c) => c.id),
        settore_ids: (promozione.settori || []).map((s) => s.id),
      });
    }
  }, [promozione]);

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: async (data: PromoFormData) => {
      const payload: any = {
        nome: data.nome,
        codice: data.codice || undefined,
        descrizione: data.descrizione || undefined,
        tipo: data.tipo,
        target_tipo: data.tipo === 'tag_temporale' ? 'offerta' : data.target_tipo,
        valore: data.tipo === 'tag_temporale' ? 0 : parseFloat(data.valore),
        valido_da: data.valido_da,
        valido_a: data.valido_a || null,
        is_attiva: data.is_attiva,
        priorita: parseInt(data.priorita) || 0,
        impatta_prezzo: data.tipo === 'tag_temporale' ? false : data.impatta_prezzo,
      };
      // Invia i link solo se l'utente può vedere l'anagrafica: altrimenti il
      // picker non è mostrato e un array vuoto cancellerebbe i link esistenti.
      if (canViewCanali) payload.canale_ids = data.canale_ids;
      if (canViewSettori) payload.settore_ids = data.settore_ids;

      if (data.target_tipo === 'prodotto') {
        payload.target_prodotto_id = data.target_prodotto_id || undefined;
      } else if (data.target_tipo === 'categoria') {
        payload.target_categoria = data.target_categoria || undefined;
      } else if (data.target_tipo === 'marca') {
        payload.target_marche = data.target_marche;
      }

      if (isNew) {
        return promozioniAPI.createPromozione(payload);
      } else {
        return promozioniAPI.updatePromozione(id!, payload);
      }
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['promozioni'] });
      queryClient.invalidateQueries({ queryKey: ['promozioni-attive'] });
      toast.success(isNew ? 'Promozione creata' : 'Promozione aggiornata');
      if (isNew) {
        navigate(`/promotions/${res.data.id}`);
      } else {
        queryClient.invalidateQueries({ queryKey: ['promozione', id] });
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    },
  });

  // Assign clients mutation
  const assignMutation = useMutation({
    mutationFn: (clienteIds: string[]) => promozioniAPI.assignClienti(id!, clienteIds),
    onSuccess: () => {
      refetchClienti();
      queryClient.invalidateQueries({ queryKey: ['promozione', id] });
      toast.success('Clienti associati');
      setShowClientModal(false);
    },
    onError: () => toast.error("Errore nell'associazione"),
  });

  // Remove client mutation
  const removeMutation = useMutation({
    mutationFn: (clienteId: string) => promozioniAPI.removeCliente(id!, clienteId),
    onSuccess: () => {
      refetchClienti();
      queryClient.invalidateQueries({ queryKey: ['promozione', id] });
      toast.success('Cliente rimosso');
    },
    onError: () => toast.error('Errore nella rimozione'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.tipo !== 'tag_temporale' && formData.target_tipo === 'prodotto' && !formData.target_prodotto_id) {
      toast.error('Seleziona il prodotto a cui applicare la promozione.');
      return;
    }
    if (formData.tipo !== 'tag_temporale' && formData.target_tipo === 'categoria' && !formData.target_categoria) {
      toast.error('Seleziona la categoria a cui applicare la promozione.');
      return;
    }
    if (formData.tipo !== 'tag_temporale' && formData.target_tipo === 'marca' && formData.target_marche.length === 0) {
      toast.error('Seleziona almeno una marca a cui applicare la promozione.');
      return;
    }
    if (formData.valido_a && formData.valido_a < formData.valido_da) {
      toast.error('La data di fine validità deve essere successiva alla data di inizio.');
      return;
    }
    saveMutation.mutate(formData);
  };

  const updateField = (field: keyof PromoFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Filter available clients (not already assigned)
  const availableClients = allClients.filter(
    (c) => !assignedClients.some((ac) => ac.cliente_id === c.id)
  );

  const filteredAvailableClients = clientSearch
    ? availableClients.filter((c) => {
        const q = clientSearch.toLowerCase();
        const name = c.tipo === 'azienda' ? c.ragione_sociale : `${c.nome} ${c.cognome || ''}`;
        return (name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
      })
    : availableClients;

  // --- Riepilogo (live summary) ---
  const valoreNum = parseFloat(formData.valore);
  const valoreLabel = formData.tipo === 'tag_temporale' ? 'Tag' : !isNaN(valoreNum)
    ? formData.tipo === 'sconto_percentuale'
      ? `-${valoreNum}%`
      : `€ ${valoreNum.toFixed(2)}`
    : '—';
  const tipoLabel =
    TIPO_OPTIONS.find((opt) => opt.value === formData.tipo)?.label;
  const TargetIcon = formData.tipo === 'tag_temporale' ? Tags : TARGET_ICONS[formData.target_tipo];
  const targetSummary = (() => {
    if (formData.tipo === 'tag_temporale') return "Offerta · selezione manuale";
    if (formData.target_tipo === 'prodotto') {
      if (!formData.target_prodotto_id) return 'Nessun prodotto selezionato';
      return targetProduct?.nome || 'Caricamento…';
    }
    if (formData.target_tipo === 'categoria') {
      return formData.target_categoria || 'Nessuna categoria selezionata';
    }
    return formData.target_marche.length > 0
      ? formData.target_marche.join(', ')
      : 'Nessuna marca selezionata';
  })();
  const validitySummary = formData.valido_da
    ? `Dal ${formatDate(formData.valido_da)}${
        formData.valido_a ? ` al ${formatDate(formData.valido_a)}` : ' · senza scadenza'
      }`
    : 'Non impostata';

  const selectedClassificazione = [
    ...(canali || []).filter((c) => formData.canale_ids.includes(c.id)),
    ...(settori || []).filter((st) => formData.settore_ids.includes(st.id)),
  ];

  if (!isNew && loadingPromo) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/promotions')}
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5 text-gray-500" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isNew ? 'Nuova Promozione' : readOnly ? 'Dettaglio Promozione' : 'Modifica Promozione'}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {readOnly && !isNew
              ? 'Sola lettura — non hai i permessi per modificare questa promozione.'
              : 'Configura il tipo di promozione e il periodo di validità.'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
          {/* Colonna principale */}
          <div className="space-y-6 lg:col-span-2">
            {/* Dettagli */}
            <SectionCard
              title="Dettagli"
              description="Nome e informazioni di base della promozione."
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className={labelClass}>Nome *</label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => updateField('nome', e.target.value)}
                    className={inputClass}
                    placeholder="Es. Sconto estivo 10%"
                    required
                    disabled={readOnly}
                  />
                </div>
                <div>
                  <label className={labelClass}>Codice</label>
                  <input
                    type="text"
                    value={formData.codice}
                    onChange={(e) => updateField('codice', e.target.value)}
                    className={inputClass}
                    placeholder="Es. PROMO-ESTATE"
                    maxLength={50}
                    disabled={readOnly}
                  />
                  <p className={hintClass}>Codice interno opzionale per identificarla.</p>
                </div>
              </div>

              <div>
                <label className={labelClass}>Descrizione</label>
                <textarea
                  value={formData.descrizione}
                  onChange={(e) => updateField('descrizione', e.target.value)}
                  className={inputClass}
                  rows={3}
                  placeholder="Note visibili agli operatori…"
                  disabled={readOnly}
                />
              </div>
            </SectionCard>

            {/* Sconto */}
            <SectionCard
              title="Tipo di promozione"
              description="Scegli uno sconto oppure un tag temporale da associare manualmente alle offerte."
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {TIPO_OPTIONS.map((opt) => (
                  <OptionCard
                    key={opt.value}
                    selected={formData.tipo === opt.value}
                    disabled={readOnly}
                    onClick={() => setFormData((prev) => ({
                      ...prev,
                      tipo: opt.value,
                      target_tipo: opt.value === 'tag_temporale' ? 'offerta' : prev.target_tipo === 'offerta' ? 'prodotto' : prev.target_tipo,
                      impatta_prezzo: opt.value === 'tag_temporale' ? false : prev.tipo === 'tag_temporale' ? true : prev.impatta_prezzo,
                    }))}
                    icon={opt.icon}
                    title={opt.label}
                    description={opt.description}
                  />
                ))}
              </div>

              {formData.tipo !== 'tag_temporale' && <>
                <div className="max-w-xs">
                  <label className={labelClass}>Valore *</label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={formData.valore}
                      onChange={(e) => updateField('valore', e.target.value)}
                      className={`${inputClass} pr-10`}
                      required
                      disabled={readOnly}
                    />
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-gray-500">
                      {formData.tipo === 'sconto_percentuale' ? '%' : '€'}
                    </span>
                  </div>
                  <p className={hintClass}>
                    {formData.tipo === 'sconto_percentuale'
                      ? 'Percentuale di sconto rispetto al prezzo di listino.'
                      : 'Prezzo finale di vendita, al netto del listino.'}
                  </p>
                </div>

                <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Applica sconto al prezzo</p>
                    <p className="mt-0.5 text-xs text-gray-500">
                      Se disattivato, la promozione viene solo censita sull'offerta (a fini di
                      analisi) senza modificare i prezzi.
                    </p>
                  </div>
                  <Toggle
                    checked={formData.impatta_prezzo}
                    disabled={readOnly}
                    onChange={(v) => updateField('impatta_prezzo', v)}
                  />
                </div>
              </>}
            </SectionCard>

            {/* Target */}
            {formData.tipo !== 'tag_temporale' && (
              <SectionCard
                title="Target"
                description="Decidi a quali articoli si applica la promozione."
              >
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {TARGET_OPTIONS.map((opt) => (
                    <OptionCard
                      key={opt.value}
                      selected={formData.target_tipo === opt.value}
                      disabled={readOnly}
                      onClick={() => updateField('target_tipo', opt.value)}
                      icon={opt.icon}
                      title={opt.label}
                      description={opt.description}
                    />
                  ))}
                </div>

                {formData.target_tipo === 'prodotto' && (
                  <div>
                    <label className={labelClass}>Prodotto *</label>
                    <ProductSearchCombobox
                      value={formData.target_prodotto_id}
                      onChange={(productId) => updateField('target_prodotto_id', productId)}
                      disabled={readOnly}
                      required
                    />
                    <p className={hintClass}>
                      La ricerca avviene sul catalogo completo: digita per filtrare i risultati.
                    </p>
                  </div>

            )}

              {formData.target_tipo === 'categoria' && (
                <div className="max-w-md">
                  <label className={labelClass}>Categoria *</label>
                  <select
                    value={formData.target_categoria}
                    onChange={(e) => updateField('target_categoria', e.target.value)}
                    className={inputClass}
                    required
                    disabled={readOnly}
                  >
                    <option value="">Seleziona categoria…</option>
                    {(categories || []).map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.target_tipo === 'marca' && (
                <div className="max-w-md">
                  <label className={labelClass}>Marche *</label>
                  <BrandMultiSelect
                    brands={brands || []}
                    selected={formData.target_marche}
                    onChange={(selected) => updateField('target_marche', selected)}
                    disabled={readOnly}
                  />
                  <p className={hintClass}>
                    Puoi selezionare più marche: la promozione si applica a tutti gli articoli di
                    ciascuna marca selezionata.
                  </p>
                </div>
              )}
            </SectionCard>

            )}

            {/* Validità */}
            <SectionCard
              title="Validità"
              description="Periodo in cui la promozione è applicabile."
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <label className={labelClass}>Valido da *</label>
                  <input
                    type="date"
                    value={formData.valido_da}
                    onChange={(e) => updateField('valido_da', e.target.value)}
                    className={inputClass}
                    required
                    disabled={readOnly}
                  />
                </div>
                <div>
                  <label className={labelClass}>Valido a</label>
                  <input
                    type="date"
                    value={formData.valido_a}
                    min={formData.valido_da || undefined}
                    onChange={(e) => updateField('valido_a', e.target.value)}
                    className={inputClass}
                    disabled={readOnly}
                  />
                  <p className={hintClass}>Lascia vuoto per nessuna scadenza.</p>
                </div>
                {formData.tipo !== 'tag_temporale' && <div>
                  <label className={labelClass}>Priorità</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.priorita}
                    onChange={(e) => updateField('priorita', e.target.value)}
                    className={inputClass}
                    disabled={readOnly}
                  />
                  <p className={hintClass}>
                    Le promozioni si applicano in ordine di priorità, dalla più alta.
                  </p>
                </div>}
              </div>

              <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">Promozione attiva</p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    Se disattivata, non viene applicata anche nel periodo di validità.
                  </p>
                </div>
                <Toggle
                  checked={formData.is_attiva}
                  disabled={readOnly}
                  onChange={(v) => updateField('is_attiva', v)}
                />
              </div>
            </SectionCard>

            {/* Classificazione */}
            {(canViewCanali || canViewSettori) && (
              <SectionCard
                title="Classificazione"
                description="Canali e settori merceologici a cui appartiene la promozione. Usati per filtrare e raggruppare l'elenco promozioni."
              >
                {canViewCanali && (
                  <div>
                    <label className={labelClass}>Canali</label>
                    <ColorChipPicker
                      items={canali || []}
                      selected={formData.canale_ids}
                      onChange={(ids) => updateField('canale_ids', ids)}
                      disabled={readOnly}
                      emptyText="Nessun canale configurato."
                    />
                  </div>
                )}
                {canViewSettori && (
                  <div>
                    <label className={labelClass}>Settori merceologici</label>
                    <ColorChipPicker
                      items={settori || []}
                      selected={formData.settore_ids}
                      onChange={(ids) => updateField('settore_ids', ids)}
                      disabled={readOnly}
                      emptyText="Nessun settore merceologico configurato."
                    />
                  </div>
                )}
              </SectionCard>
            )}
          </div>

          {/* Riepilogo */}
          <aside className="lg:sticky lg:top-6">
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-100 px-5 py-4">
                <h2 className="text-base font-semibold text-gray-900">Riepilogo</h2>
              </div>

              <div className="space-y-4 px-5 py-4">
                <div>
                  <p className="truncate text-sm font-medium text-gray-900">
                    {formData.nome || 'Senza titolo'}
                  </p>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="text-3xl font-bold tracking-tight text-gray-900">
                      {valoreLabel}
                    </span>
                    <span className="text-sm text-gray-500">{tipoLabel}</span>
                  </div>
                </div>

                <dl className="space-y-3 border-t border-gray-100 pt-4">
                  <div className="flex items-start justify-between gap-3">
                    <dt className="flex items-center gap-1.5 text-sm text-gray-500">
                      <TargetIcon className="h-4 w-4 shrink-0" />
                      Target
                    </dt>
                    <dd className="text-right text-sm font-medium text-gray-900">
                      {targetSummary}
                    </dd>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <dt className="flex items-center gap-1.5 text-sm text-gray-500">
                      <Calendar className="h-4 w-4 shrink-0" />
                      Validità
                    </dt>
                    <dd className="text-right text-sm font-medium text-gray-900">
                      {validitySummary}
                    </dd>
                  </div>
                  {formData.tipo !== 'tag_temporale' && <div className="flex items-start justify-between gap-3">
                    <dt className="text-sm text-gray-500">Priorità</dt>
                    <dd className="text-sm font-medium tabular-nums text-gray-900">
                      {formData.priorita || '0'}
                    </dd>
                  </div>}
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-sm text-gray-500">Stato</dt>
                    <dd>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          formData.is_attiva
                            ? 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20'
                            : 'bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-500/20'
                        }`}
                      >
                        {formData.is_attiva ? 'Attiva' : 'Non attiva'}
                      </span>
                    </dd>
                  </div>
                  {selectedClassificazione.length > 0 && (
                    <div className="flex items-start justify-between gap-3">
                      <dt className="text-sm text-gray-500">Classificazione</dt>
                      <dd className="flex flex-wrap justify-end gap-1">
                        {selectedClassificazione.map((item) => (
                          <span
                            key={item.id}
                            className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                            style={{ backgroundColor: item.color, color: contrastColor(item.color) }}
                          >
                            {item.nome}
                          </span>
                        ))}
                      </dd>
                    </div>
                  )}
                  {!formData.impatta_prezzo && (
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-sm text-gray-500">Impatto prezzo</dt>
                      <dd>
                        <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20">
                          Solo tracciamento
                        </span>
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              {!readOnly && (
                <div className="space-y-2 border-t border-gray-100 px-5 py-4">
                  <button
                    type="submit"
                    disabled={saveMutation.isPending}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saveMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    {isNew ? 'Crea Promozione' : 'Salva Modifiche'}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/promotions')}
                    className="w-full rounded-lg px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100"
                  >
                    Annulla
                  </button>
                </div>
              )}
            </div>
          </aside>
        </div>
      </form>

      {/* Allegati section */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
        <div className="flex items-center gap-2">
          <Paperclip className="h-5 w-5 text-gray-500" />
          <h2 className="text-base font-semibold text-gray-900">Allegati</h2>
        </div>
        {isNew ? (
          <p className="rounded-lg border border-dashed border-gray-200 py-3 text-center text-sm italic text-gray-500">
            Salva la promozione per gestire gli allegati.
          </p>
        ) : (
          <PromozioneAllegatiManager promozioneId={id!} />
        )}
      </div>

      {/* Client Assignment Section (only for product promotions) */}
      {!isNew && formData.tipo !== 'tag_temporale' && (
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Clienti Associati</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Se nessun cliente viene associato, la promozione si applica a tutti.
              </p>
            </div>
            {!readOnly && (
              <button
                onClick={() => {
                  setShowClientModal(true);
                  setClientSearch('');
                }}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 text-sm"
              >
                <UserPlus className="h-4 w-4" />
                Aggiungi Clienti
              </button>
            )}
          </div>

          {assignedClients.length === 0 ? (
            <p className="text-sm text-gray-400 italic py-4 text-center">
              Nessun cliente associato - la promozione si applica a tutti i clienti.
            </p>
          ) : (
            <div className="divide-y divide-gray-100">
              {assignedClients.map((ac) => (
                <div key={ac.id} className="flex items-center justify-between py-2.5">
                  <div>
                    <span className="text-sm font-medium text-gray-900">{ac.cliente_nome}</span>
                    {ac.cliente_email && (
                      <span className="text-xs text-gray-500 ml-2">{ac.cliente_email}</span>
                    )}
                  </div>
                  {!readOnly && (
                    <button
                      onClick={() => {
                        if (confirm(`Rimuovere ${ac.cliente_nome} dalla promozione?`)) {
                          removeMutation.mutate(ac.cliente_id);
                        }
                      }}
                      className="p-1 text-gray-400 hover:text-red-600 rounded"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Client Assignment Modal */}
      {showClientModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Aggiungi Clienti</h3>
              <button
                onClick={() => setShowClientModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cerca cliente..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {filteredAvailableClients.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">Nessun cliente disponibile</p>
              ) : (
                <div className="space-y-1">
                  {filteredAvailableClients.slice(0, 50).map((c) => {
                    const name =
                      c.tipo === 'azienda'
                        ? c.ragione_sociale || c.nome
                        : `${c.nome} ${c.cognome || ''}`.trim();
                    return (
                      <button
                        key={c.id}
                        onClick={() => assignMutation.mutate([c.id])}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-blue-50 text-sm flex items-center justify-between"
                      >
                        <div>
                          <span className="font-medium text-gray-900">{name}</span>
                          {c.email && (
                            <span className="text-gray-500 ml-2 text-xs">{c.email}</span>
                          )}
                        </div>
                        <UserPlus className="h-4 w-4 text-blue-500" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
